import moment from 'moment';
import React, { useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/InfoOutlined';

import { PatientLivingArrangement } from '@vestahealthcare/common/enums';
import { translate } from '@vestahealthcare/common/i18n';
import {
  ExternalIdSource,
  MemberExternalId,
  MemberPhone,
  Patient,
  SelfDirectingContact,
} from '@vestahealthcare/common/models';
import {
  EMPTY,
  NO_TIME_DATE_FORMAT_DO,
} from '@vestahealthcare/common/utils/constants';

import {
  Card,
  CardContent,
  CardHeader,
  Colors,
  IconButton,
  Tooltip,
} from 'styleguide-v2';

import {
  PatientParam,
  UpdateMemberPhoneParams,
} from 'dash/src/services/PatientServices';

import { DetailColumn, DetailItem } from '../utils';
import { MemberAddressDetail } from './MemberAddressDetail';
import { MemberEmailDetail } from './MemberEmailDetail';
import { MemberExternalIdsDetail } from './MemberExternalIdsDetail';
import { MemberLanguagesDetail } from './MemberLanguagesDetail';
import { MemberPersonalEditModal } from './MemberPersonalEditModal';
import { MemberPhonesDetail } from './MemberPhonesDetail';
import { MemberSelfDirectingDetail } from './MemberSelfDirectingDetail';

type Props = {
  data: {
    externalIdSources: ExternalIdSource[];
  };
  member: Patient;
  memberExternalIds: MemberExternalId[];
  onSubmit: (
    params: Partial<
      { member: PatientParam } & {
        proxyContact: SelfDirectingContact;
      } & UpdateMemberPhoneParams & { externalIds: MemberExternalId[] }
    >,
  ) => Promise<boolean>;
  phoneNumbers?: MemberPhone[];
  selfDirectingContactInfo?: SelfDirectingContact;
};

export const MemberPersonalDetails = ({
  data,
  member,
  memberExternalIds,
  phoneNumbers,
  selfDirectingContactInfo,
  onSubmit,
}: Props) => {
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);

  return (
    <>
      <Card>
        <CardHeader
          title={
            <div className="flex gap">
              <span>
                {translate('personalDetails.personalDetailsCard.title')}
              </span>
              <br />
              <IconButton
                disabled={!member?.isEditable()}
                noMargin
                onClick={() => setOpenEditModal(true)}
                size="small"
              >
                <EditIcon />
              </IconButton>
            </div>
          }
        />
        <CardContent>
          <div className="grid-wrapper fit">
            <DetailColumn>
              <DetailItem namespace="personalDetailsCard" label="memberName">
                {member.fullName}
              </DetailItem>
              <DetailItem namespace="personalDetailsCard" label="memberId">
                {member.id}
              </DetailItem>
              <MemberExternalIdsDetail externalIds={memberExternalIds} />
              <DetailItem namespace="personalDetailsCard" label="birthDate">
                {member.birthDate
                  ? `${moment(member.birthDate).format(
                      NO_TIME_DATE_FORMAT_DO,
                    )} (${translate('common.yearsOld', { count: member.age })})`
                  : undefined}
              </DetailItem>
              <DetailItem namespace="personalDetailsCard" label="deceasedDate">
                {member.deceasedDate
                  ? moment(member.deceasedDate).format(NO_TIME_DATE_FORMAT_DO)
                  : undefined}
              </DetailItem>
              <DetailItem
                namespace="personalDetailsCard"
                label="gender"
                noMarginBottom
              >
                {member.gender?.toString()}
              </DetailItem>
            </DetailColumn>

            <DetailColumn>
              <DetailItem namespace="personalDetailsCard" label="residence">
                {[
                  member.residenceType?.toString() || EMPTY,
                  member?.residenceTypeOther,
                ]
                  .filter(Boolean)
                  .join(' - ')}
              </DetailItem>
              <DetailItem namespace="personalDetailsCard" label="home">
                <MemberAddressDetail address={member.address} />
              </DetailItem>
              <DetailItem namespace="personalDetailsCard" label="livesWith">
                {member.livingArrangement
                  ?.map((l) =>
                    [
                      l.toString(),
                      l === PatientLivingArrangement.OTHER &&
                        member.livingArrangementOther,
                    ]
                      .filter(Boolean)
                      .join(' - '),
                  )
                  .filter(Boolean)
                  .join(', ')}
              </DetailItem>

              <MemberSelfDirectingDetail
                contactInfo={selfDirectingContactInfo}
                member={member}
              />
            </DetailColumn>
            <DetailColumn>
              <DetailItem namespace="personalDetailsCard" label="language">
                <MemberLanguagesDetail
                  preferred={member.language}
                  languages={member.languages}
                />
              </DetailItem>
              <MemberPhonesDetail phones={phoneNumbers} />
              <DetailItem
                namespace="personalDetailsCard"
                label="email"
                noMarginBottom
                icon={
                  <Tooltip
                    text={translate(
                      'personalDetails.personalDetailsCard.emailTooltip',
                    )}
                  >
                    <InfoIcon htmlColor={Colors.iconGreen} />
                  </Tooltip>
                }
              >
                <MemberEmailDetail email={member?.email} />
              </DetailItem>
            </DetailColumn>
          </div>
        </CardContent>
      </Card>
      <MemberPersonalEditModal
        data={data}
        externalIds={memberExternalIds}
        member={member}
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        onSubmit={onSubmit}
        phoneNumbers={phoneNumbers}
        selfDirectingContact={selfDirectingContactInfo}
      />
    </>
  );
};
